import { useState, useEffect, useRef } from "react";
import { Modal } from "antd";
import {
  CloseOutlined,
  CloseCircleOutlined,
  ArrowLeftOutlined,
  LockOutlined,
  LoginOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { modalStyles } from "../../../utils/modalStyles";
import { useViewport } from "../../../hooks/useViewport";

// Кодът е с ПРОИЗВОЛНА дължина (както при вход с код на сервитьорите) — може да е
// по-малко или повече от 6 символа. Точките са минимум толкова и растат с кода.
const MIN_DOTS = 4;

// Огледало на org валидацията (`features/auth/schemas.py`, `PIN_FORBIDDEN`): кодът е
// 1..64 знака — цифри, букви (вкл. кирилица) и символи, БЕЗ интервали и < > " ' ` \
const MAX_CODE_LENGTH = 64;
const FORBIDDEN_CODE_CHARS = /[\s<>"'`\\]/;

const appendCodeChar = (previous: string, character: string) => {
  if (previous.length >= MAX_CODE_LENGTH) return previous;
  if (FORBIDDEN_CODE_CHARS.test(character)) return previous;
  return previous + character;
};

const R = {
  surface: "var(--color-surface)",
  bg: "var(--color-canvas)",
  border: "var(--color-shade-2)",
  borderMed: "var(--color-shade-3)",
  ink: "var(--color-ink)",
  text: "var(--color-ink)",
  textSoft: "var(--color-body)",
  textMuted: "var(--color-muted)",
  brand: "#D97706",
  brandDeep: "var(--color-brand-deep-ink)",
  brandTint: "var(--color-brand-tint)",
};

const keypadRows = [
  ["1", "2", "3"],
  ["4", "5", "6"],
  ["7", "8", "9"],
];

interface RestaurantAccessModalProps {
  open: boolean;
  onClose: () => void;
  onCodeSubmit: (code: string) => Promise<void> | void;
  loading?: boolean;
}

export const RestaurantAccessModal = ({
  open,
  onClose,
  onCodeSubmit,
  loading = false,
}: RestaurantAccessModalProps) => {
  const { t } = useTranslation();
  const { isMobile, isNarrow, isTablet } = useViewport();
  const [code, setCode] = useState("");
  // Реф с последния код — за коректен Enter от клавиатурата (без stale closure).
  const codeRef = useRef("");
  codeRef.current = code;
  const submitRef = useRef<(val: string) => void>(() => {});
  const cancelRef = useRef<() => void>(() => {});

  useEffect(() => {
    if (!open) setCode("");
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (loading) return;

      // Не прихващай клавиши, когато фокусът е в поле за въвеждане — иначе чуждо
      // писане се трупа като „код" (същата защита като в CodeKeypad).
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName;
      if (
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        tag === "SELECT" ||
        target?.isContentEditable
      ) {
        return;
      }

      if (e.key === "Backspace") {
        setCode((p) => p.slice(0, -1));
        return;
      }
      if (e.key === "Enter") {
        submitRef.current(codeRef.current);
        return;
      }
      if (e.key === "Escape") {
        // Без preventDefault същият Esc минава и през глобалната навигация „назад".
        e.preventDefault();
        cancelRef.current();
        return;
      }
      // Кодът е като личния ПИН на сервитьора — цифри, букви (вкл. кирилица) и
      // символи; org валидацията отхвърля само интервалите и опасните знаци.
      if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
        setCode((p) => appendCodeChar(p, e.key));
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, loading]);

  // Няма авто-submit — кодът е с произволна дължина, потвърждава се с бутон/Enter.
  submitRef.current = (val: string) => {
    if (loading || val.length === 0) return;
    void onCodeSubmit(val);
    setCode("");
  };
  cancelRef.current = () => {
    setCode("");
    onClose();
  };

  const handleNumber = (n: string) => {
    if (loading) return;
    setCode((p) => appendCodeChar(p, n));
  };
  const handleBackspace = () => {
    if (loading) return;
    setCode((p) => p.slice(0, -1));
  };
  const handleClear = () => {
    if (loading) return;
    setCode("");
  };

  // Точките растат с кода (произволна дължина) — минимум MIN_DOTS гнезда.
  const dotCount = Math.max(MIN_DOTS, code.length);

  // Width per §8.5 — fixed classes, not per-breakpoint fork
  const width = isMobile ? undefined : isTablet ? 420 : 460;

  const headerPad = isNarrow ? "14px 16px" : "20px 24px";
  const bodyPad = isNarrow ? "16px" : "20px 24px 24px";
  const keypadGap = isNarrow ? 10 : 12;
  const btnHeight = isNarrow ? 52 : 56;
  const slotHeight = isNarrow ? 48 : 56;

  return (
    <Modal
      open={open}
      onCancel={cancelRef.current}
      footer={null}
      centered
      closable={false}
      destroyOnHidden
      width={width}
      styles={modalStyles}
      style={{ maxWidth: "calc(100vw - 32px)" }}
    >
      <div
        style={{
          background: R.surface,
          fontFamily:
            '"Plus Jakarta Sans", -apple-system, BlinkMacSystemFont, system-ui, sans-serif',
          color: R.text,
        }}
      >
        {/* Header */}
        <div
          className="flex items-start"
          style={{
            padding: headerPad,
            gap: 12,
            borderBottom: `1px solid ${R.border}`,
          }}
        >
          {/* Icon chip */}
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: 10,
              background: R.brandTint,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <LockOutlined style={{ fontSize: 20, color: R.brandDeep }} />
          </div>

          <div style={{ flex: 1, minWidth: 0 }}>
            <h2
              style={{
                margin: 0,
                fontSize: 18,
                fontWeight: 700,
                letterSpacing: -0.3,
                color: R.ink,
                lineHeight: 1.2,
              }}
            >
              {t("modules.accessModal.title")}
            </h2>
            <p
              style={{
                margin: "4px 0 0",
                fontSize: 13,
                fontWeight: 500,
                color: R.textSoft,
                lineHeight: 1.4,
              }}
            >
              {t("modules.accessModal.subtitle")}
            </p>
          </div>

          {/* Close chip */}
          <button
            type="button"
            onClick={cancelRef.current}
            aria-label={t("modules.accessModal.close")}
            style={{
              height: 30,
              width: 30,
              borderRadius: 6,
              background: "transparent",
              border: "none",
              color: R.textMuted,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "background 120ms, color 120ms",
              flexShrink: 0,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "var(--color-shade-1)";
              e.currentTarget.style.color = R.ink;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "transparent";
              e.currentTarget.style.color = R.textMuted;
            }}
          >
            <CloseOutlined style={{ fontSize: 14 }} />
          </button>
        </div>

        {/* Body */}
        <div
          style={{
            padding: bodyPad,
            display: "flex",
            flexDirection: "column",
            gap: isNarrow ? 14 : 18,
          }}
        >
          {/* Code dots — произволна дължина, минимум MIN_DOTS, растат с кода */}
          <div
            className="flex flex-wrap items-center justify-center"
            style={{ gap: isNarrow ? 10 : 12, minHeight: slotHeight }}
          >
            {Array.from({ length: dotCount }).map((_, i) => {
              const filled = i < code.length;
              return (
                <motion.span
                  key={i}
                  initial={false}
                  animate={{ scale: code.length === i + 1 ? [1, 1.18, 1] : 1 }}
                  transition={{ duration: 0.16 }}
                  style={{
                    width: 16,
                    height: 16,
                    borderRadius: 999,
                    background: filled ? R.brand : R.bg,
                    border: `1px solid ${filled ? R.brand : R.borderMed}`,
                    transition:
                      "background 160ms ease, border-color 160ms ease",
                  }}
                />
              );
            })}
          </div>

          {/* Keypad */}
          <div
            className="grid"
            style={{
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: keypadGap,
            }}
          >
            {keypadRows.flat().map((n) => (
              <KeypadButton
                key={n}
                label={n}
                onClick={() => handleNumber(n)}
                disabled={loading}
                height={btnHeight}
              />
            ))}

            <KeypadButton
              aria-label={t("modules.accessModal.clear")}
              icon={<CloseCircleOutlined style={{ fontSize: 20 }} />}
              onClick={handleClear}
              disabled={loading || code.length === 0}
              height={btnHeight}
            />
            <KeypadButton
              label="0"
              onClick={() => handleNumber("0")}
              disabled={loading}
              height={btnHeight}
            />
            <KeypadButton
              aria-label={t("modules.accessModal.backspace")}
              icon={<ArrowLeftOutlined style={{ fontSize: 20 }} />}
              onClick={handleBackspace}
              disabled={loading || code.length === 0}
              height={btnHeight}
            />
          </div>

          {/* Primary submit — кодът е с произволна дължина, потвърждава се тук/Enter */}
          <button
            type="button"
            onClick={() => submitRef.current(code)}
            disabled={loading || code.length === 0}
            style={{
              width: "100%",
              height: btnHeight,
              borderRadius: 10,
              background: loading || code.length === 0 ? R.bg : R.brand,
              border: "none",
              color: loading || code.length === 0 ? R.textMuted : "#FFFFFF",
              fontSize: 15,
              fontWeight: 700,
              fontFamily: "inherit",
              cursor: loading || code.length === 0 ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              transition: "background 140ms ease, color 140ms ease",
            }}
          >
            {loading ? <LoadingOutlined spin /> : <LoginOutlined />}
            {t("modules.accessModal.submit")}
          </button>

          {/* Secondary close button */}
          <button
            type="button"
            onClick={cancelRef.current}
            style={{
              width: "100%",
              height: btnHeight,
              borderRadius: 10,
              background: "transparent",
              border: `1px solid ${R.border}`,
              color: R.textSoft,
              fontSize: 14,
              fontWeight: 600,
              fontFamily: "inherit",
              cursor: "pointer",
              transition: "background 120ms, color 120ms, border-color 120ms",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "var(--color-shade-1)";
              e.currentTarget.style.color = R.ink;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "transparent";
              e.currentTarget.style.color = R.textSoft;
            }}
          >
            {t("modules.accessModal.close")}
          </button>
        </div>
      </div>
    </Modal>
  );
};

type KeypadButtonProps = {
  label?: string;
  icon?: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  height: number;
  "aria-label"?: string;
};

function KeypadButton({
  label,
  icon,
  onClick,
  disabled,
  height,
  ...rest
}: KeypadButtonProps) {
  return (
    <motion.button
      type="button"
      whileTap={disabled ? undefined : { scale: 0.97 }}
      transition={{ duration: 0.08 }}
      onClick={onClick}
      onMouseDown={(e) => e.preventDefault()}
      disabled={disabled}
      aria-label={rest["aria-label"]}
      style={{
        height,
        width: "100%",
        borderRadius: 12,
        background: R.surface,
        border: `1px solid ${R.border}`,
        color: R.ink,
        fontSize: 22,
        fontWeight: 700,
        fontFamily: "inherit",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.4 : 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        transition:
          "background 140ms ease, border-color 140ms ease, color 140ms ease",
        userSelect: "none",
      }}
      onMouseEnter={(e) => {
        if (disabled) return;
        e.currentTarget.style.background = R.brandTint;
        e.currentTarget.style.borderColor = "rgba(217,119,6,0.32)";
        e.currentTarget.style.color = R.brandDeep;
      }}
      onMouseLeave={(e) => {
        if (disabled) return;
        e.currentTarget.style.background = R.surface;
        e.currentTarget.style.borderColor = R.border;
        e.currentTarget.style.color = R.ink;
      }}
    >
      {icon ?? label}
    </motion.button>
  );
}
